import { call, put, takeLatest } from "redux-saga/effects";
import { ApiError } from "@/lib/apiClient";
import { PaginatedApiResponse } from "@/lib/apiClient";
import { Product } from "@/types/models/product";
import {
  attachWholesalerProductFailure,
  attachWholesalerProductRequest,
  attachWholesalerProductSuccess,
  detachWholesalerProductFailure,
  detachWholesalerProductRequest,
  detachWholesalerProductSuccess,
  fetchWholesalerProductFailure,
  fetchWholesalerProductRequest,
} from "../slices/wholesalerProducts.slice";
import {
  getWholesalersProducts,
  importWholesalersProducts,
  removeWholesalersProducts,
} from "../actions/wholesaler.actions";
import { fetchWholesalerProductSuccess } from "../slices/wholesalerProducts.slice";
import { toast } from "sonner";

function* fetchWholesalerProductsSaga(
  action: ReturnType<typeof fetchWholesalerProductRequest>,
): Generator<unknown, void, unknown> {
  try {
    const data = (yield call(getWholesalersProducts, {
      id: action.payload.id,
      page: action.payload.page,
      pageSize: action.payload.pageSize,
    })) as PaginatedApiResponse<Product[]>;
    yield put(
      fetchWholesalerProductSuccess({
        id: action.payload.id,
        data: data.data,
        pagination: {
          page: data.pagination.page,
          total: data.pagination.total,
          pageSize: data.pagination.pageSize,
          totalPages: data.pagination.totalPages,
        },
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "Something went wrong!",
          );
    toast.error(normalized.message || "Something went wrong!");
    yield put(
      fetchWholesalerProductFailure({
        id: action.payload.id,
        error: normalized,
      }),
    );
  }
}
function* addWholesalerProductsSaga(
  action: ReturnType<typeof attachWholesalerProductRequest>,
) {
  try {
    const response = (yield call(importWholesalersProducts, {
      id: action.payload.id,
      margin: action.payload.margin,
      margin_type: action.payload.margin_type,
      payment_type: action.payload.payment_type,
      product_ids: action.payload.product_ids,
    })) as { message?: string };
    toast.success(response?.message || "Products imported successfully");
    yield put(
      attachWholesalerProductSuccess({
        id: action.payload.id,
        data: action.payload.product_ids,
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "Something went wrong!",
          );
    toast.error(normalized.message || "Something went wrong!");
    yield put(
      attachWholesalerProductFailure({
        id: action.payload.id,
        error: normalized,
      }),
    );
  }
}
function* removeWholesalerProductsSaga(
  action: ReturnType<typeof detachWholesalerProductRequest>,
) {
  try {
    yield call(removeWholesalersProducts, {
      id: action.payload.id,
      product_ids: action.payload.product_ids,
    });
    yield put(
      detachWholesalerProductSuccess({
        id: action.payload.id,
        data: action.payload.product_ids,
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "Something went wrong!",
          );
    toast.error(normalized.message || "Something went wrong!");
    yield put(
      detachWholesalerProductFailure({
        id: action.payload.id,
        error: normalized,
      }),
    );
  }
}

export default function* wholesalerProductsRootSaga() {
  yield takeLatest(
    fetchWholesalerProductRequest.type,
    fetchWholesalerProductsSaga,
  );
  yield takeLatest(
    attachWholesalerProductRequest.type,
    addWholesalerProductsSaga,
  );
  yield takeLatest(
    detachWholesalerProductRequest.type,
    removeWholesalerProductsSaga,
  );
}
